/**
 * Reads the policy list.
 *
 * Straight from Firestore rather than through a callable, because that is how
 * the list already works — the previous client read `orgs/{orgId}/policies`
 * directly and the security rules are written for it.
 *
 * THE QUERIES ARE SHAPED BY THE RULES, and they have to be. firestore.rules
 * grants reads on `policies` in two clauses, each testing
 * `resource.data.status`:
 *
 *     allow read: if sameOrg() && hasAnyRole([viewer..]) && status == 'published'
 *     allow read: if sameOrg() && hasAnyRole([author..]) && status != 'published'
 *
 * For a single document Firestore evaluates that against the document. For a
 * LIST it cannot — there is no document yet — so it instead requires the query
 * to be constrained such that every possible result would satisfy some clause.
 * An unconstrained `getDocs(collection(...))` therefore fails with
 * permission-denied even for an administrator, because Firestore cannot prove
 * in advance that the results would all be readable.
 *
 * So the constraint is issued explicitly, once per clause the caller's roles
 * unlock, and the results are merged. This is not an optimization; without it
 * the page shows nothing to everyone.
 *
 * Every field mapping was checked against real documents. The previous
 * client's model class was not a reliable guide: it described the shape it
 * wanted
 * after its own translation step, and several names differ on the wire.
 */

import { calendarDate } from '../../shared/dates';
import { useQuery } from '@tanstack/react-query';
import {
  collection,
  getDocs,
  getFirestore,
  query,
  where,
} from 'firebase/firestore';
import { app } from '../../lib/firebase';
import type { Policy, PolicyStatus } from '../../types/policy';
import { POLICY_STATUSES } from '../../types/policy';
import type { Role } from '../../types/roles';

const db = getFirestore(app);

/** A Firestore Timestamp, a Date, or an ISO string, flattened to "YYYY-MM-DD". */
/** A date-only value, read in UTC. One rule, in shared/dates.ts. */
const toIsoDate = calendarDate;

/**
 * Coerce a stored status.
 *
 * Real data contains one document whose status is `in_review`, which nothing
 * writes today and which the previous client also failed to match. It falls
 * back to draft here for the same reason it does there: a row that renders
 * beats a list that throws, and a stray value should not be promoted to
 * "published" by accident.
 */
function toStatus(value: unknown): PolicyStatus {
  return typeof value === 'string' && (POLICY_STATUSES as readonly string[]).includes(value)
    ? (value as PolicyStatus)
    : 'draft';
}

function toStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * Department names for a policy.
 *
 * Two shapes are in play. `departmentNames` is the newer snapshot array and
 * exists on a handful of documents; `department` is an embedded object with a
 * `name`, present on most. Neither is on all of them, so both are tried.
 */
/**
 * The category name, from whichever shape the document uses.
 *
 * `categoryName` on a handful of documents, an embedded `category` object on
 * most — the same split as departments, and for the same reason: the snapshot
 * fields were added after most of this data was written.
 */
function toCategoryName(d: Record<string, unknown>): string | null {
  const name = d.categoryName;
  if (typeof name === 'string' && name) return name;

  const cat = d.category;
  if (typeof cat === 'object' && cat !== null) {
    const n = (cat as { name?: unknown }).name;
    if (typeof n === 'string' && n) return n;
  }
  if (typeof cat === 'string' && cat) return cat;
  return null;
}

function toDepartmentNames(d: Record<string, unknown>): string[] {
  const names = toStringArray(d.departmentNames);
  if (names.length) return names;

  const dept = d.department;
  if (typeof dept === 'object' && dept !== null) {
    const name = (dept as { name?: unknown }).name;
    if (typeof name === 'string' && name) return [name];
  }
  if (typeof dept === 'string' && dept) return [dept];
  return [];
}

export function toPolicy(
  id: string,
  d: Record<string, unknown>,
  memberNames: Map<string, string>,
  activeMembers?: ReadonlySet<string>,
): Policy {
  const ownerId = typeof d.ownerId === 'string' ? d.ownerId : null;

  return {
    id,
    policyNumber: typeof d.policyNumber === 'string' ? d.policyNumber : '',
    title: typeof d.title === 'string' ? d.title : '(untitled)',
    status: toStatus(d.status),
    revisionStatus: d.revisionStatus === 'draft' || d.revisionStatus === 'in_review' || d.revisionStatus === 'approved' ? d.revisionStatus : null,
    departmentNames: toDepartmentNames(d),
    categoryName: toCategoryName(d),
    regulatoryStandardLabels: toStringArray(d.regulatoryStandardLabels),
    // A raw uid in an Owner column tells nobody anything, so an unresolvable
    // one becomes null and renders as an em dash.
    ownerName: ownerId ? (memberNames.get(ownerId) ?? null) : null,
    /**
     * The owner is no longer somebody who can act. Only decided when the set
     * of active members was actually loaded; without it the answer is "no",
     * because a reader who cannot list members must not be told every policy
     * is orphaned.
     */
    ownerHasLeft: activeMembers ? !ownerId || !activeMembers.has(ownerId) : false,
    // `nextReviewDate` is what is stored. `reviewDate` is what saveDraft
    // accepts as INPUT and what the previous client called its field; nothing writes
    // it, so reading it returns null for every policy in the database.
    reviewDate: toIsoDate(d.nextReviewDate),
  };
}

/**
 * Who the members are: their names for the Owner column, and which of them are
 * still ACTIVE, which is what tells a policy whose owner has left.
 */
async function loadMembers(orgId: string): Promise<{ names: Map<string, string>; active: Set<string> | undefined }> {
  const names = new Map<string, string>();
  let active: Set<string> | undefined = new Set<string>();
  try {
    const snap = await getDocs(collection(db, 'orgs', orgId, 'members'));
    snap.docs.forEach((doc) => {
      const data = doc.data();
      const name = typeof data.displayName === 'string' ? data.displayName : data.email;
      if (typeof name === 'string' && name) names.set(doc.id, name);
      if (data.status === 'active') active!.add(doc.id);
    });
  } catch {
    // A role that cannot list members still gets a policy list; the Owner
    // column is hidden from those roles anyway, so this is not worth failing
    // the whole page over. `active` is UNKNOWN, not empty: an empty set says
    // nobody is active, and every policy was then marked as having lost its
    // owner for exactly the people who cannot see owners at all.
    active = undefined;
  }
  return { names, active };
}

/**
 * EVERY PUBLISHED POLICY, for a picker that has to offer all of them.
 *
 * This is what is left of the loader the policy library used before it was
 * paged (see library/). The library no longer calls it. One caller remains,
 * the exception form's "which policy" picker, and it is the only reason this
 * is still here: it reads the whole published library to fill a dropdown,
 * which is the pattern the library was rebuilt to stop. It should become a
 * search-as-you-type picker; until then this stays small and says so.
 */
export function usePolicies(orgId: string | null, _roles: readonly Role[], _scope: 'published') {
  return useQuery({
    queryKey: ['policies-published', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<Policy[]> => {
      const policies = collection(db, 'orgs', orgId!, 'policies');
      const [members, snap] = await Promise.all([loadMembers(orgId!), getDocs(query(policies, where('status', '==', 'published')))]);
      return snap.docs.map((doc) => toPolicy(doc.id, doc.data(), members.names, members.active));
    },
  });
}
